// src/pages/profile-settings/patient/EditIntro.jsx
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import supabase from "@/services/supabaseClient";
import CoverCropModal from "@/components/media/CoverCropModal.jsx";
import Divider from "@/components/ui/Divider.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { clearAvatarSessionCache, readAvatarSessionCache, writeAvatarSessionCache } from "@/lib/avatarSessionCache.js";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { useTheme } from "@/contexts/ThemeContext.jsx";
import { PATIENT_SETTINGS_PROFILE_ROUTE } from "@/pages/profile-settings/settingsNavigation.js";

import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import Sheet from "@/sot/primitives/Sheet.jsx";
import SaveChip from "@/sot/primitives/SaveChip.jsx";
import CoverPhoto from "@/sot/primitives/CoverPhoto.jsx";
import AvatarHeaderPositioned from "@/sot/primitives/AvatarHeaderPositioned.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import FolaOnboardingCard from "@/pages/profile-settings/FolaOnboardingCard.jsx";
import BasicDialogCritical from "@/components/ui/BasicDialogCritical.jsx";
import Card from "@/components/ui/Card.jsx";

import {
  clearPatientIntroPreview,
  loadPatientIntroPreview,
  savePatientIntroPreview,
} from "@/lib/patientIntroPreview";
import { normalizeConditionList } from "@/utils/conditions";
import { resolveDependantAvatarSrc } from "@/services/dependantCareService.js";
import { hydrateDependantIdentityStatuses } from "@/services/dependantIdentityVerificationService.js";

// ---------------------------------------------------------------------------
// Icon paths
// ---------------------------------------------------------------------------

const COVER_IMAGE_LIGHT = "/assets/images/patient_profile_cover.png?v=20260323";
const COVER_IMAGE_DARK = "/assets/images/patient_profile_cover.svg?v=20260323";
const ICON_VERIFIED_CHIP = "/icons/doctor/verified_badge.svg";
const ICON_PENDING_VERIFICATION = "/icons/doctor/pending-verification-badge.svg";

const ICON_PHONE = "/icons/patient/call.svg";
const ICON_EMAIL = "/icons/patient/mail.svg";
const ICON_NATIONAL_ID = "/icons/patient/fingerprint.svg";

const ICON_HEALTH_INSURANCE = "/icons/patient/health_and_safety.svg";
const ICON_DEPENDANT_CHILD = "/icons/dependant_child.svg";
const ICON_DEPENDANT_ADULT = "/icons/dependant_adult.svg";

const ICON_ADDRESS = "/icons/patient/Address.svg";
// postal code reuses mail icon
const ICON_POSTAL = "/icons/patient/mail.svg";
const ICON_DISTANCE = "/icons/patient/distance.svg";

const ICON_MORE_VERT = "/icons/patient/more_vert.svg";
const ICON_EDIT_SQUARE = "/icons/doctor/avatar_edit_button.svg";
const ICON_DELETE = "/icons/patient/delete.svg";
const ICON_DELETE_CRITICAL = "/icons/doctor/delete_critical.svg";

const ICON_BLOODTYPE = "/icons/patient/bloodtype.svg";
const ICON_ALLERGY = "/icons/patient/allergy.svg";
const ICON_CONDITIONS = "/icons/patient/conditions.svg";
const ICON_PULMONOLOGY = "/icons/patient/pulmonology.svg";
const ICON_HEIGHT_WEIGHT = "/icons/height-weight.svg";

const ICON_UPLOAD_IMAGE = "/icons/patient/upload_image.svg";
const ICON_TAKE_PHOTO = "/icons/patient/take_photo.svg";

const DEV_PHOTO_KEY = "afrimed:dev:patient_profile_photo";
const DEPENDANTS_DEV_KEY = "afrimed:dev:patient_dependants";
const INSURANCE_DEV_KEY = "afrimed:dev:patient_insurance";

const PATIENT_BUCKET = "patient-profile-images";

function InsetDivider() {
  return <Divider variant="outlineVariant" inset className="opacity-[0.35]" />;
}

function RailDivider() {
  return <Divider variant="outlineVariant" className="-mx-4 md:mx-0" />;
}
const PATIENT_AVATAR_BUCKET = "patient-profile-images";

function isLocalDevHost() {
  try {
    const host = window.location.hostname;
    return (
      import.meta.env.DEV &&
      (host === "localhost" ||
        host === "127.0.0.1" ||
        host === "0.0.0.0" ||
        host.endsWith(".local"))
    );
  } catch {
    return false;
  }
}

function hasIntroPreviewContent(preview) {
  if (!preview || typeof preview !== "object") return false;
  return [
    preview.name,
    preview.full_name,
    preview.firstName,
    preview.lastName,
    preview.dobIso,
    preview.date_of_birth,
    preview.phone,
    preview.phone_number,
    preview.email,
    preview.nationalId,
    preview.national_id,
    preview.national_id_number,
    preview.idPassport,
    preview.idNumber,
    preview.id_number,
  ].some((value) => textFromUnknown(value));
}

function loadPatientProfileTableDraft() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem("afrimed_onb_preview_v1:patient_profiles");
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

// ---------------------------------------------------------------------------
// Small SVGs
// ---------------------------------------------------------------------------

function ChevronRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M6 3.5 10.5 8 6 12.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AddIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M8 3v10M3 8h10"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Primitive rows / chips
// ---------------------------------------------------------------------------

// Generic row with optional icon, value + chevron + extra right content
function InfoRow({
  iconSrc,
  label,
  value,
  showChevron = true,
  extraRight,
  onClick,
  isPlaceholder = false,
  iconAvatar = false,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center px-4 py-3 text-left"
    >
      {iconSrc && (
        <span
          className={[
            "mr-3 inline-flex items-center justify-center overflow-hidden",
            iconAvatar
              ? "h-8 w-8 rounded-full border border-[rgba(var(--clr-outline-variant),0.55)] bg-[rgb(var(--clr-surface))] shadow-[0px_0px_0px_1px_rgba(24,50,119,0.10),0px_6px_12px_-4px_rgba(29,79,215,0.06)]"
              : "h-6 w-6",
          ].join(" ")}
        >
          <img src={iconSrc} alt="" className={iconAvatar ? "h-full w-full object-cover" : "h-6 w-6"} />
        </span>
      )}

      <div className="flex-1 min-w-0">
        {label && (
          <div className="text-label-medium text-on-surface-var">
            {label}
          </div>
        )}
        <div
          className={`text-body-large ${
            isPlaceholder ? "text-on-surface-var" : "text-on-surface"
          } truncate`}
        >
          {value}
        </div>
      </div>

      {extraRight && (
        <span className="ml-1 inline-flex items-center">{extraRight}</span>
      )}

      {showChevron && (
        <span className="ml-3 inline-flex h-5 w-5 items-center justify-center text-on-surface-var">
          <ChevronRightIcon />
        </span>
      )}
    </button>
  );
}

// Simple “Verified” pill
function VerifiedChip({ alt = "Verified" }) {
  return (
    <img
      src={ICON_VERIFIED_CHIP}
      alt={alt}
      className="h-[20px] w-auto select-none pointer-events-none"
    />
  );
}

// Simple “Pending verification” pill – icon asset already includes text
function PendingChip({ alt = "Pending verification" }) {
  return (
    <span className="inline-flex items-center">
      <img
        src={ICON_PENDING_VERIFICATION}
        alt={alt}
        className="h-[20px] w-auto select-none pointer-events-none"
      />
    </span>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
// ---------- DOB parsing (supports ISO + UK dd/mm/yyyy) ----------
function parseDobToLocalDate(dob) {
  if (!dob) return null;

  // If it's already a Date
  if (dob instanceof Date && !Number.isNaN(dob.getTime())) return dob;

  const raw = textFromUnknown(dob);
  if (!raw) return null;

  // 1) ISO: YYYY-MM-DD (best case)
  // NOTE: new Date("YYYY-MM-DD") is treated as UTC in many browsers,
  // so we construct a local date manually to avoid day shifts.
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const y = Number(iso[1]);
    const m = Number(iso[2]); // 1-12
    const d = Number(iso[3]); // 1-31
    // Midday local avoids DST edge cases
    return new Date(y, m - 1, d, 12, 0, 0, 0);
  }

  // 2) UK: DD/MM/YYYY
  const uk = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (uk) {
    const d = Number(uk[1]);
    const m = Number(uk[2]); // 1-12
    const y = Number(uk[3]);
    return new Date(y, m - 1, d, 12, 0, 0, 0);
  }

  // 3) Fallback: let Date try (for "July 26, 1994" etc.)
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function calcAgeFromDob(dob) {
  const d = parseDobToLocalDate(dob);
  if (!d) return null;

  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();

  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;

  // Guard against negative / insane ages from bad input
  if (age < 0 || age > 130) return null;

  return age;
}

function pickDependantIcon(d) {
  const relRaw = String(d?.relationship || "").toLowerCase().trim();
  const rel = relRaw.replace(/\s+/g, " ");

  // Some data comes as "Child (10)" etc — treat by prefix/contains
  const isParent =
    rel.includes("parent") || rel.includes("mother") || rel.includes("father") || rel.includes("guardian");

  const isPartner = rel.includes("spouse") || rel.includes("partner") || rel.includes("wife") || rel.includes("husband");

  // child-like relationships (but we still use age when available)
  const isChildLike =
    rel.startsWith("child") || rel.includes("son") || rel.includes("daughter");

  // siblings can be minors — DO NOT force adult
  const isSiblingLike =
    rel.startsWith("sibling") || rel.includes("brother") || rel.includes("sister");

  const age = calcAgeFromDob(d?.date_of_birth || d?.dob);

  if (["sibling", "brother", "sister"].includes(rel)) {
  return age !== null && age <= 14 ? ICON_DEPENDANT_CHILD : ICON_DEPENDANT_ADULT;
}

  // Always-adult categories
  if (isParent || isPartner) return ICON_DEPENDANT_ADULT;

  // Age-based categories
  if (age !== null) {
    return age <= 14 ? ICON_DEPENDANT_CHILD : ICON_DEPENDANT_ADULT;
  }

  // No DOB available: fall back to relationship hint
  if (isChildLike) return ICON_DEPENDANT_CHILD;

  // Unknown/other: default adult (least risky)
  return ICON_DEPENDANT_ADULT;
}

function formatDateDisplay(value, locale = "en-GB") {
  if (!value) return "";
  try {
    const d = parseDobToLocalDate(value) || new Date(value);
    if (Number.isNaN(d.getTime())) return textFromUnknown(value);

    return d.toLocaleDateString(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return textFromUnknown(value);
  }
}

function formatInsuranceSummary(row) {
  const provider = String(row?.provider || row?.insurance_provider || "").trim();
  const member = String(
    row?.membership_number ||
      row?.insurance_member_id ||
      row?.insurance_policy_number ||
      ""
  ).trim();
  const plan = String(row?.plan_type || row?.insurance_plan || "").trim();

  const bits = [provider, member, plan].filter(Boolean);
  return bits.length ? bits.join(" · ") : "";
}

function cleanText(value) {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.toLowerCase() === "[object object]" ? "" : trimmed;
  }
  if (typeof value === "number") return String(value);
  return "";
}

function textFromUnknown(value) {
  if (Array.isArray(value)) return value.map(textFromUnknown).filter(Boolean).join(", ");
  if (value && typeof value === "object") {
    return (
      cleanText(value.label) ||
      cleanText(value.language) ||
      cleanText(value.name) ||
      cleanText(value.value) ||
      cleanText(value.text) ||
      cleanText(value.title) ||
      cleanText(value.country) ||
      cleanText(value.code) ||
      ""
    );
  }
  return cleanText(value);
}

function listFromUnknown(value) {
  if (Array.isArray(value)) {
    return value
      .map((entry) => textFromUnknown(entry))
      .filter(Boolean);
  }

  if (value && typeof value === "object") {
    const one = textFromUnknown(value);
    return one ? [one] : [];
  }

  const text = cleanText(value);
  if (!text) return [];
  return text
    .split(",")
    .map((part) => cleanText(part))
    .filter(Boolean);
}

function boolFromUnknown(value) {
  if (typeof value === "boolean") return value;
  const text = cleanText(value).toLowerCase();
  if (!text) return null;
  if (["yes", "true", "1", "registered"].includes(text)) return true;
  if (["no", "false", "0", "not registered"].includes(text)) return false;
  return null;
}

function loadDevDependants() {
  try {
    const raw = localStorage.getItem(DEPENDANTS_DEV_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

const FRENCH_PATIENT_LANGUAGE_LABELS = {
  English: "Anglais",
  French: "Français",
  Lingala: "Lingala",
  Swahili: "Swahili",
  Tshiluba: "Tshiluba",
  Kikongo: "Kikongo",
  Afrikaans: "Afrikaans",
  Tswana: "Tswana",
  Zulu: "Zoulou",
  Xhosa: "Xhosa",
};

function isFrenchLocale(languageCode) {
  return String(languageCode || "").toLowerCase().startsWith("fr");
}

function localizePatientLanguageLabel(value, languageCode) {
  const text = String(value || "").trim();
  if (!isFrenchLocale(languageCode)) return text;
  return FRENCH_PATIENT_LANGUAGE_LABELS[text] || text;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function EditIntro() {
  const { isDark } = useTheme();
  const nav = useNavigate();
  const location = useLocation();
  const { user: authUser, refreshProfile, profile: authProfile } = useAuth() || {};
  const { t, languageCode } = useLanguage();
  const isFrench = isFrenchLocale(languageCode);
  const localeCode =
    isFrench
      ? "fr-FR"
      : languageCode === "sn"
      ? "sn-ZW"
      : languageCode === "zu"
        ? "zu-ZA"
        : "en-GB";

  const [insuranceSummary, setInsuranceSummary] = React.useState("");
  const [insuranceLoaded, setInsuranceLoaded] = React.useState(false);

  // ✅ MISSING STATE (add this)
  const [dependantsRows, setDependantsRows] = React.useState([]);
  const [dependantsLoaded, setDependantsLoaded] = React.useState(false);

  const [pendingPhotoPath, setPendingPhotoPath] = React.useState("");
  const [avatarFile, setAvatarFile] = React.useState(null);
  const [avatarRemoved, setAvatarRemoved] = React.useState(false);
  const [removeAvatarConfirmOpen, setRemoveAvatarConfirmOpen] = React.useState(false);
  const avatarRemovedRef = React.useRef(false);
  const authAvatarUrl = String(
    authProfile?.profile_image_url || authProfile?.avatar_url || ""
  ).trim();
  const cachedAvatarUrl = React.useMemo(
    () => readAvatarSessionCache({ role: "patient", userId: authUser?.id }),
    [authUser?.id]
  );

  // Avatar file inputs (for profile photo)
const fileInputRef = React.useRef(null);
const cameraInputRef = React.useRef(null);

  // ✅ SAFE alias (must come AFTER dependantsRows exists)
  const safeDependantsRows = Array.isArray(dependantsRows) ? dependantsRows : [];


  // ------------------------------------------------------------
  // Insurance summary (Profile card)
  // ------------------------------------------------------------

  function isBypassAuth() {
    const bypassFlag = String(import.meta.env.VITE_BYPASS_AUTH || "").toLowerCase().trim();
    if (bypassFlag !== "true" && bypassFlag !== "1") return false;
    if (!import.meta.env.DEV) return false;

    const host = typeof window !== "undefined" ? window.location.hostname : "";
    const isLocal =
      host === "localhost" ||
      host === "127.0.0.1" ||
      host === "0.0.0.0" ||
      host.endsWith(".local");

    return isLocal;
  }

  function loadDevInsurance() {
    try {
      const raw = localStorage.getItem(INSURANCE_DEV_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

    // ------------------------------------------------------------
  // Cover + Avatar picking (keep refs BEFORE any usage)
  // ------------------------------------------------------------

  // Cover picker state (minimal to compile; you can wire CropModal later)
  const [coverPickerOpen, setCoverPickerOpen] = React.useState(false);
  const [coverUploading, setCoverUploading] = React.useState(false);
  const [coverError, setCoverError] = React.useState("");

  // Cover file inputs (MUST be declared before handlers)
  const avatarFileInputRef = React.useRef(null);
  const avatarCameraInputRef = React.useRef(null);

  // Avatar crop modal state (patient)
  const [avatarSrc, setAvatarSrc] = React.useState(null);
  const [showAvatarCrop, setShowAvatarCrop] = React.useState(false);

  function openCoverPicker() {
    if (coverUploading) return;
    setCoverError("");
    setCoverPickerOpen(true);
  }

  function closeCoverPicker() {
    setCoverPickerOpen(false);
  }

  function handleAvatarUploadClick() {
    if (coverUploading) return;
   avatarFileInputRef.current?.click();
  }

  function handleAvatarTakePhotoClick() {
    if (coverUploading) return;
   avatarCameraInputRef.current?.click();
  }

  function onAvatarFilePicked(e) {
  const file = e.target.files?.[0];
  if (!file) return;

  setAvatarFile(file); // ✅ keep the real file for upload

  const reader = new FileReader();
  reader.onload = () => {
    setAvatarSrc(String(reader.result));
    setShowAvatarCrop(true);
    closePicker();
  };
  reader.readAsDataURL(file);

  e.target.value = "";
}

  // (Optional) If any legacy JSX still expects these names, keep safe refs:
  // IMPORTANT: do NOT alias to undefined refs.
  // If your JSX needs fileInputRef/cameraInputRef for AVATAR, define them later where avatar inputs live.

  React.useEffect(() => {
    let alive = true;

    async function loadInsuranceSummary() {
      try {
        // 1) BYPASS mode: read localStorage (NO supabase, NO session errors)
        if (isBypassAuth()) {
          const devRow = loadDevInsurance();
          const summary = devRow ? formatInsuranceSummary(devRow) : "";
          if (alive) setInsuranceSummary(summary);
          return;
        }

        const {
          data: { user },
          error: authErr,
        } = await supabase.auth.getUser();

        if (authErr || !user) {
          if (alive) setInsuranceSummary("");
          return;
        }

        const { data, error } = await supabase
          .from("patient_profiles")
          .select("insurance_provider, insurance_policy_number, insurance_member_id, insurance_plan")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error) throw error;

        const summary = data ? formatInsuranceSummary(data) : "";
        if (alive) setInsuranceSummary(summary);
      } catch {
        // silent failure (no red explosions)
        if (alive) setInsuranceSummary("");
      } finally {
        if (alive) setInsuranceLoaded(true);
      }
    }

    loadInsuranceSummary();

    return () => {
      alive = false;
    };
  }, [location.key]);

// ------------------------------------------------------------
// Dependants list (refresh on mount + when returning from Dependants)
// ------------------------------------------------------------
React.useEffect(() => {
  let alive = true;

  async function loadDependantsList() {
    try {
      if (!alive) return;
      setDependantsLoaded(false);

      // BYPASS mode
      if (isBypassAuth()) {
        const list = loadDevDependants();
        if (!alive) return;
        setDependantsRows(Array.isArray(list) ? list : []);
        return;
      }

      const {
        data: { user },
        error: authErr,
      } = await supabase.auth.getUser();

      if (authErr || !user) {
        if (alive) setDependantsRows([]);
        return;
      }

      const { data, error } = await supabase
        .from("patient_dependants")
        .select(
          "id, first_name, last_name, verification_status, relationship, date_of_birth, avatar_path, avatar_url"
        )
        .eq("user_id", user.id)
        .order("created_at", { ascending: true });

      if (error) throw error;
      
      const hydrated = await hydrateDependantIdentityStatuses(supabase, Array.isArray(data) ? data : []);
      if (alive) setDependantsRows(hydrated);
    } catch {
      if (alive) setDependantsRows([]);
    } finally {
      if (alive) setDependantsLoaded(true);
    }
  }

  loadDependantsList();

  // ✅ If we returned from Dependants, clear the flag once (no loops)
  if (location.state?.dependantsUpdated) {
    nav(location.pathname, { replace: true, state: null });
  }

  return () => {
    alive = false;
  };

  // ✅ Reload when screen remounts OR when dependantsUpdated flips on return
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [location.key, location.state?.dependantsUpdated]);

  // DB profile
  const [profile, setProfile] = React.useState(null);
  const [sessionUser, setSessionUser] = React.useState(null);

  // photo + auth
  const [loading, setLoading] = React.useState(true);
  const [uploading, setUploading] = React.useState(false);
  const [previewUrl, setPreviewUrl] = React.useState(() => {
  try {
    const host = window.location.hostname;
    const isLocal =
      host === "localhost" ||
      host === "127.0.0.1" ||
      host === "0.0.0.0" ||
      host.endsWith(".local");
  
  
  // ✅ Only allow DEV photo override on localhost during DEV
    if (!import.meta.env.DEV || !isLocal) return "";

    const devPhoto = localStorage.getItem(DEV_PHOTO_KEY);
    return devPhoto || cachedAvatarUrl || authAvatarUrl || "";
  } catch {
    return cachedAvatarUrl || authAvatarUrl || "";
  }
});
  const [userId, setUserId] = React.useState(null);
  const [error, setError] = React.useState("");

  const [introPreview, setIntroPreview] = React.useState(() => {
    return loadPatientIntroPreview() || {};
  });

  // Snackbars
  const [saved, setSaved] = React.useState(false);
  const [saveError, setSaveError] = React.useState("");

  // languages kebab menu state (UI only)
  const [languageMenuOpen, setLanguageMenuOpen] = React.useState(false);
  const [selectedLanguage, setSelectedLanguage] = React.useState("");

  // ── Load profile + photo from Supabase ───────────────────────────────────
  React.useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const {
          data: { user },
          error: authError,
        } = await supabase.auth.getUser();

        if (authError) throw authError;
        if (!user) {
          if (!cancelled) setError("You need to be signed in.");
          return;
        }

        if (cancelled) return;
        setUserId(user.id);
        setSessionUser(user);
        if (cachedAvatarUrl || authAvatarUrl) {
          setPreviewUrl((current) => current || cachedAvatarUrl || authAvatarUrl);
        }

        // Keep local onboarding/settings preview only when it belongs to this user.
        // This preserves the immediate post-onboarding Settings handoff without
        // allowing stale local profile data to cross between test accounts.
        try {
          const preview = loadPatientIntroPreview() || {};
          const hasPreview = hasIntroPreviewContent(preview);
          const previewUserId = cleanText(preview.user_id || preview.userId);
          const previewEmail = cleanText(preview.email || preview.user_email).toLowerCase();
          const currentEmail = cleanText(user.email).toLowerCase();
          const belongsToUser =
            !hasPreview ||
            previewUserId === user.id ||
            (previewEmail && previewEmail === currentEmail) ||
            (!previewUserId && !previewEmail && isLocalDevHost());

          if (belongsToUser) {
            const nextPreview =
              hasPreview && (!previewUserId || !previewEmail)
                ? savePatientIntroPreview({
                    ...preview,
                    user_id: user.id,
                    email: preview.email || user.email || "",
                  })
                : preview;
            setIntroPreview(nextPreview || {});
          } else {
            clearPatientIntroPreview();
            setIntroPreview({});
          }
        } catch {}

        const [
          { data: profileRow, error: profError },
          { data: authProfileRow },
          { data: ehrInfoRow },
        ] = await Promise.all([
          supabase
            .from("patient_profiles")
            .select(
              [
                "profile_photo_path",
                "first_name",
                "last_name",
                "full_name",
                "date_of_birth",
                "phone",
                "phone_number",
                "phone_verified_at",
                "email",
                "email_verified_at",
                "national_id",
                "national_id_number",
                "verification_status",
                "street_address",
                "city",
                "postal_code",
                "country",
                "blood_type",
                "allergies",
                "medical_conditions",
                "organ_donor",
                "languages",
              ].join(",")
            )
            .eq("user_id", user.id)
            .maybeSingle(),
          supabase
            .from("profiles")
            .select("user_id,display_name,avatar_url,first_name,last_name")
            .eq("user_id", user.id)
            .maybeSingle(),
          supabase
            .from("ehr_patient_info")
            .select("patient_id,date_of_birth,primary_language,height_cm,weight_kg,blood_type,current_medications,allergies,chronic_conditions,is_organ_donor")
            .eq("patient_id", user.id)
            .maybeSingle(),
        ]);

        if (profError) throw profError;

        if (!cancelled) {
          const metadata = user.user_metadata || {};
          const metadataName = cleanText(
            metadata.full_name ||
              metadata.name ||
              [metadata.first_name, metadata.last_name].map(textFromUnknown).filter(Boolean).join(" ")
          );
          const authProfileName = cleanText(
            authProfileRow?.display_name ||
              [authProfileRow?.first_name, authProfileRow?.last_name]
                .map(textFromUnknown)
                .filter(Boolean)
                .join(" ")
          );
          const rowName = cleanText(
            profileRow?.full_name ||
              [profileRow?.first_name, profileRow?.last_name]
                .map(textFromUnknown)
                .filter(Boolean)
                .join(" ")
          );

          setProfile(
            profileRow || authProfileRow || ehrInfoRow
              ? {
                  ...(authProfileRow || {}),
                  ...(profileRow || {}),
                  full_name: rowName || authProfileName || metadataName || null,
                  first_name:
                    profileRow?.first_name ||
                    authProfileRow?.first_name ||
                    metadata.first_name ||
                    null,
                  last_name:
                    profileRow?.last_name ||
                    authProfileRow?.last_name ||
                    metadata.last_name ||
                    null,
                  email: profileRow?.email || user.email || null,
                  date_of_birth: ehrInfoRow?.date_of_birth || profileRow?.date_of_birth || null,
                  primary_language: ehrInfoRow?.primary_language || null,
                  height_cm: ehrInfoRow?.height_cm ?? profileRow?.height_cm ?? null,
                  weight_kg: ehrInfoRow?.weight_kg ?? profileRow?.weight_kg ?? null,
                  blood_type: ehrInfoRow?.blood_type || profileRow?.blood_type || null,
                  current_medications:
                    ehrInfoRow?.current_medications ||
                    profileRow?.current_medications ||
                    profileRow?.medications ||
                    null,
                  allergies: ehrInfoRow?.allergies || profileRow?.allergies || null,
                  chronic_conditions:
                    ehrInfoRow?.chronic_conditions || profileRow?.chronic_conditions || profileRow?.medical_conditions || null,
                  organ_donor:
                    typeof ehrInfoRow?.is_organ_donor === "boolean"
                      ? ehrInfoRow.is_organ_donor
                      : profileRow?.organ_donor ?? profileRow?.is_organ_donor ?? null,
                  is_organ_donor:
                    typeof ehrInfoRow?.is_organ_donor === "boolean"
                      ? ehrInfoRow.is_organ_donor
                      : profileRow?.is_organ_donor ?? profileRow?.organ_donor ?? null,
                }
              : null
          );
        }

        // photo
        if (profileRow?.profile_photo_path && !cancelled) {
          const storage = supabase.storage.from(PATIENT_BUCKET);
          const { data: signed, error: signedErr } = await storage
            .createSignedUrl(profileRow.profile_photo_path, 60 * 60 * 24);

          const fallbackPublicUrl = (() => {
            const { data } = storage.getPublicUrl(profileRow.profile_photo_path);
            return data?.publicUrl || "";
          })();

          const resolvedUrl = (!signedErr && signed?.signedUrl) || fallbackPublicUrl;
          if (resolvedUrl && !cancelled && !avatarRemovedRef.current) {
            writeAvatarSessionCache({ role: "patient", userId: user.id, src: resolvedUrl });
            // only use Supabase photo if we don't have a dev override
            try {
              const host = window.location.hostname;
              const isLocal =
                host === "localhost" ||
                host === "127.0.0.1" ||
                host === "0.0.0.0" ||
                host.endsWith(".local");

              // ✅ In staging/prod: ALWAYS show Supabase photo if it exists
              if (!import.meta.env.DEV || !isLocal) {
                setPreviewUrl(resolvedUrl);
              } else {
                // DEV localhost: only use Supabase photo if no dev override exists
                const devPhoto = localStorage.getItem(DEV_PHOTO_KEY);
                if (!devPhoto) setPreviewUrl(resolvedUrl);
              }
            } catch {
              setPreviewUrl(resolvedUrl);
            }
          }
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setError(
            t("patient.editIntro.errors.loadProfile", isFrench ? "Impossible de charger votre profil." : "We couldn’t load your profile."),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [authAvatarUrl, cachedAvatarUrl, t, isFrench]);

  // --- Derived values: preview → DB → placeholder --------------------------
  const tableDraft = React.useMemo(() => loadPatientProfileTableDraft(), [introPreview]);

  // Name
  const previewName = cleanText(
    introPreview.full_name ||
      introPreview.name ||
      [introPreview.firstName, introPreview.lastName].map(textFromUnknown).filter(Boolean).join(" ")
  );
  const dbName = cleanText(
    profile?.full_name ||
      profile?.display_name ||
      [profile?.first_name, profile?.last_name].map(textFromUnknown).filter(Boolean).join(" ")
  );
  const nameIsPlaceholder = !previewName && !dbName;
  const nameValue =
    previewName ||
    dbName ||
    t("patient.editIntro.placeholders.fullName", "Add your full name");

  // DOB
  const previewDobIso = cleanText(
    introPreview.dobIso ||
      introPreview.date_of_birth ||
      introPreview.dob ||
      tableDraft.date_of_birth ||
      ""
  );
  const dbDobIso = cleanText(profile?.date_of_birth);
  const dobFromPreview = previewDobIso
    ? formatDateDisplay(previewDobIso, localeCode)
    : "";
  const dobFromDb = dbDobIso ? formatDateDisplay(dbDobIso, localeCode) : "";
  const dobIsPlaceholder = !dobFromPreview && !dobFromDb;
  const dobValue =
    dobFromPreview ||
    dobFromDb ||
    t("patient.editIntro.placeholders.dob", "Add your date of birth");

  // Phone
  const previewPhone = cleanText(introPreview.phone || introPreview.phone_number || "");
  const dbPhone = cleanText(
    profile?.phone ||
      profile?.phone_number ||
      authUser?.phone ||
      sessionUser?.phone ||
      ""
  );
  const phoneIsPlaceholder = !previewPhone && !dbPhone;
  const phoneVerified = Boolean(
    introPreview?.phoneVerified ||
      introPreview?.phone_verified ||
      profile?.phone_verified_at ||
      authUser?.phone_confirmed_at ||
      sessionUser?.phone_confirmed_at
  );
  const showPhoneVerified = phoneVerified && !phoneIsPlaceholder;
  const showPhonePending = !phoneVerified && !phoneIsPlaceholder;
  const phoneValue =
    previewPhone ||
    dbPhone ||
    t("patient.editIntro.placeholders.phone", isFrench ? "Ajoutez votre numéro de téléphone" : "Add your phone number");

  // Email
  const previewEmail = cleanText(introPreview.email);
  const dbEmail = cleanText(profile?.email || authUser?.email || sessionUser?.email || "");
  const emailIsPlaceholder = !previewEmail && !dbEmail;
  const emailValue =
    previewEmail ||
    dbEmail ||
    t("patient.editIntro.placeholders.email", "Add your email address");

  // from patientIntroPreview (local verification state)
  const emailVerified = Boolean(
    introPreview?.emailVerified ||
      introPreview?.email_verified ||
      profile?.email_verified_at ||
      authUser?.email_confirmed_at ||
      sessionUser?.email_confirmed_at
  );
  const showEmailVerified = emailVerified && !emailIsPlaceholder;

  // National ID / Passport (single source of truth for badges)
// accepted: verified | pending | unverified
const previewId = cleanText(
  introPreview.nationalId ||
    introPreview.national_id ||
    introPreview.national_id_number ||
    introPreview.idPassport ||
    introPreview.idNumber ||
    introPreview.id_number ||
    ""
);
const dbId = cleanText(profile?.national_id || profile?.national_id_number || "");

const idIsPlaceholder = !previewId && !dbId;
const idValue =
  previewId ||
  dbId ||
  t("patient.editIntro.placeholders.idPassport", "Add your national ID or passport");

// local + DB verification flags
const idStatusRaw = cleanText(profile?.verification_status).toLowerCase();
const idPending = Boolean(introPreview?.idPending || idStatusRaw === "pending");
const idVerified = Boolean(introPreview?.idVerified || idStatusRaw === "verified");

  // Address (3 separate preview fields)
  const previewStreetLine1 = textFromUnknown(introPreview.addressLine || introPreview.addressStreet || "");
  const previewStreetLine2 = textFromUnknown(introPreview.addressLine2 || introPreview.city || "");
  const previewStreet = [previewStreetLine1, previewStreetLine2].filter(Boolean).join(", ");
  const previewPostal = textFromUnknown(introPreview.postalCode || introPreview.addressPostalCode || "");
  const previewCountry = textFromUnknown(introPreview.country || introPreview.addressCountry || "");

  const dbStreetLine1 = textFromUnknown(profile?.street_address || "");
  const dbStreetLine2 = textFromUnknown(profile?.city || "");
  const dbStreet = [dbStreetLine1, dbStreetLine2].filter(Boolean).join(", ");
  const dbPostal = textFromUnknown(profile?.postal_code || "");
  const dbCountry = textFromUnknown(profile?.country || "");

  const streetIsPlaceholder = !previewStreet && !dbStreet;
  const postalIsPlaceholder = !previewPostal && !dbPostal;
  const countryIsPlaceholder = !previewCountry && !dbCountry;

  const streetValue =
    dbStreet ||
    previewStreet ||
    t("patient.editIntro.placeholders.street", "Add your street address");
  const postalValue =
    dbPostal ||
    previewPostal ||
    t("patient.editIntro.placeholders.postalCode", "Add your postal code");
  const countryValue =
    dbCountry ||
    previewCountry ||
    t("patient.editIntro.placeholders.country", "Add your country");

  // Medical info
const previewBlood = textFromUnknown(introPreview.bloodType);
const dbBlood = textFromUnknown(profile?.blood_type);
const bloodIsPlaceholder = !previewBlood && !dbBlood;
const bloodValue =
  previewBlood ||
  dbBlood ||
  t("patient.editIntro.placeholders.bloodType", "Add your blood type");

// Allergies: preview may be array or string
const previewAllergies = listFromUnknown(introPreview.allergies).join(", ");
const dbAllergies = listFromUnknown(profile?.allergies).join(", ");
const allergiesIsPlaceholder = !previewAllergies && !dbAllergies;
const allergiesValue =
  previewAllergies ||
  dbAllergies ||
  t("patient.editIntro.placeholders.allergies", "Add your allergies");

// Conditions: preview may be array or string
const previewConditionsList = normalizeConditionList(listFromUnknown(introPreview.conditions));
const dbConditionsList = normalizeConditionList(listFromUnknown(profile?.medical_conditions));
const previewConditions = previewConditionsList.join(", ");
const dbConditions = dbConditionsList.join(", ");
const conditionsIsPlaceholder = !previewConditions && !dbConditions;
const conditionsValue =
  previewConditions ||
  dbConditions ||
  t("patient.editIntro.placeholders.conditions", "Add your medical conditions");

// Organ donor: stays string-ish ("Yes" / "No")
const previewDonor = textFromUnknown(introPreview.organDonor);
const dbDonor =
  typeof profile?.organ_donor === "boolean"
    ? profile.organ_donor
      ? "Yes"
      : "No"
    : textFromUnknown(profile?.organ_donor || "");
const donorIsPlaceholder = !previewDonor && !dbDonor;
const donorValue =
  previewDonor ||
  dbDonor ||
  t("patient.editIntro.placeholders.organDonor", "Tell us if you are an organ donor");

const previewHeight = textFromUnknown(introPreview.heightCm || introPreview.height_cm);
const previewWeight = textFromUnknown(introPreview.weightKg || introPreview.weight_kg);
const dbHeight = textFromUnknown(profile?.height_cm || profile?.height);
const dbWeight = textFromUnknown(profile?.weight_kg || profile?.weight);
const heightWeightIsPlaceholder =
  !previewHeight && !previewWeight && !dbHeight && !dbWeight;
const heightWeightValue =
  previewHeight && previewWeight
    ? `${previewHeight} cm • ${previewWeight} kg`
    : previewHeight
      ? `${previewHeight} cm`
      : previewWeight
        ? `${previewWeight} kg`
        : dbHeight && dbWeight
          ? `${dbHeight} cm • ${dbWeight} kg`
          : dbHeight
              ? `${dbHeight} cm`
              : dbWeight
                ? `${dbWeight} kg`
              : t("patient.editIntro.placeholders.heightWeight", "Add your height and weight");

  // Languages (preview first, then DB)
const previewLanguages = listFromUnknown(introPreview?.languages);
const dbLanguages = listFromUnknown(profile?.languages);
const preferredLanguage = String(
  introPreview?.primaryLanguage ||
    introPreview?.primary_language ||
    profile?.primary_language ||
    ""
).trim();
const orderLanguages = (items, preferred) => {
  const preferredClean = String(preferred || "").trim();
  const values = [
    ...(preferredClean ? [preferredClean] : []),
    ...listFromUnknown(items),
  ];
  const deduped = Array.from(
    new Map(
      values
        .map((item) => String(item || "").trim())
        .filter(Boolean)
        .map((item) => [item.toLowerCase(), item])
    ).values()
  );
  return preferredClean
    ? [
        preferredClean,
        ...deduped.filter((item) => item.toLowerCase() !== preferredClean.toLowerCase()),
      ]
    : deduped;
};
const languagesSource = orderLanguages(
  previewLanguages.length > 0 ? previewLanguages : dbLanguages,
  preferredLanguage,
);

// ✅ SAFE alias (must be declared before it's used)
const safeLanguagesSource = Array.isArray(languagesSource) ? languagesSource : [];

// ✅ immune to undefined
const hasLanguages = safeLanguagesSource.length > 0;

// UI model for rendering
const languagesUi = safeLanguagesSource
  .map((item, idx) => ({
    id: `lang-${idx}`,
    label: localizePatientLanguageLabel(textFromUnknown(item), languageCode),
    preferred: idx === 0 && !!preferredLanguage,
  }))
  .filter((x) => x.label);

// ✅ optional safe alias if you still reference it elsewhere
const safeLanguagesUi = Array.isArray(languagesUi) ? languagesUi : [];

// -------------------------------------------------------------------------
// Photo upload handlers (legacy – OPTIONAL if you now use crop-first flow)
// -------------------------------------------------------------------------
async function handleFileSelected(file) {
  if (!file) return;

  const MAX = 5 * 1024 * 1024; // 5MB
  const ALLOWED = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

  if (!ALLOWED.includes(file.type)) {
    setSaveError(
      t("patient.editIntro.errors.fileType", isFrench ? "Veuillez téléverser une image JPG, PNG ou WebP." : "Please upload a JPG, PNG or WebP image."),
    );
    return;
  }
  if (file.size > MAX) {
    setSaveError(t("patient.editIntro.errors.fileSize", "Max file size is 5MB."));
    return;
  }

  setUploading(true);
  setSaveError("");
  setSaved(false);

  try {
    const {
      data: { user },
      error: authErr,
    } = await supabase.auth.getUser();

    if (authErr || !user?.id) {
      setSaveError(
        t(
          "patient.editIntro.errors.signInToUpload",
          "You need to be signed in to upload a profile photo.",
        ),
      );
      return;
    }

    // stable private path
    const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
    const safeExt = ["jpg", "jpeg", "png", "webp"].includes(ext) ? ext : "jpg";
    const path = `private/${user.id}/profile.${safeExt}`;

    const { error: upErr } = await supabase.storage
      .from(PATIENT_BUCKET)
      .upload(path, file, { upsert: true, contentType: file.type });

    if (upErr) throw upErr;

    const { data: signed, error: signedErr } = await supabase.storage
      .from(PATIENT_BUCKET)
      .createSignedUrl(path, 60 * 60 * 24);

    if (!signedErr && signed?.signedUrl) setPreviewUrl(signed.signedUrl);

    setPendingPhotoPath(path);
    setSaved(true);
  } catch (e) {
    console.error("[Patient EditIntro] upload error:", e);
    setSaveError(
      e?.message ||
        t("patient.editIntro.errors.uploadPhoto", isFrench ? "Impossible de téléverser votre photo." : "We couldn’t upload your photo."),
    );
  } finally {
    setUploading(false);
  }
}

  function handleUploadClick() {
    if (uploading || loading) return;
    fileInputRef.current?.click();
  }

  function handleTakePhotoClick() {
    if (uploading || loading) return;
    cameraInputRef.current?.click();
  }

  // Bottom sheet open/close
  const [pickerOpen, setPickerOpen] = React.useState(false);
  function openPicker() {
    setPickerOpen(true);
  }
  function closePicker() {
    setPickerOpen(false);
  }

  const hasCustomAvatar = !avatarRemoved && Boolean(
    avatarFile ||
      pendingPhotoPath ||
      profile?.profile_photo_path ||
      profile?.profile_image_url ||
      profile?.avatar_url,
  );

  async function removeProfileAvatar() {
    const previousPath = String(pendingPhotoPath || profile?.profile_photo_path || "").trim();
    setRemoveAvatarConfirmOpen(false);
    setUploading(true);
    setSaveError("");
    avatarRemovedRef.current = true;

    try {
      const { data: authData, error: authErr } = await supabase.auth.getUser();
      const user = authData?.user;
      if (authErr) throw authErr;
      if (!user?.id) {
        throw new Error(t("auth.loginRequired", "You need to be signed in to remove a profile photo."));
      }

      // The patient profile row owns the storage path. Clear its canonical
      // reference and the legacy core alias before deleting only that object.
      const { error: profileErr } = await supabase
        .from("patient_profiles")
        .upsert(
          { user_id: user.id, profile_photo_path: null },
          { onConflict: "user_id" },
        );
      if (profileErr) throw profileErr;

      const { error: coreProfileErr } = await supabase
        .from("profiles")
        .update({ avatar_url: null })
        .eq("user_id", user.id);
      if (coreProfileErr) throw coreProfileErr;

      if (previousPath) {
        const { error: storageErr } = await supabase.storage
          .from(PATIENT_AVATAR_BUCKET)
          .remove([previousPath]);
        if (storageErr) throw storageErr;
      }

      clearAvatarSessionCache({ role: "patient", userId: user.id });
      localStorage.removeItem(DEV_PHOTO_KEY);
      await refreshProfile?.();
      setProfile((current) => current ? { ...current, profile_photo_path: null, profile_image_url: null, avatar_url: null } : current);
      setPendingPhotoPath("");
      setAvatarFile(null);
      setAvatarSrc(null);
      setAvatarRemoved(true);
      setPreviewUrl("");
      closePicker();
      setSaved(true);
    } catch (err) {
      avatarRemovedRef.current = false;
      setSaveError(
        err?.message ||
          t("patient.editIntro.errors.removePhoto", "We couldn’t remove your profile picture."),
      );
    } finally {
      setUploading(false);
    }
  }

  // Helpers: Languages kebab menu
  function openLanguageMenu(label) {
    setSelectedLanguage(label);
    setLanguageMenuOpen(true);
  }

  function closeLanguageMenu() {
    setLanguageMenuOpen(false);
  }

  function extFromFile(file) {
  const name = String(file?.name || "");
  const dot = name.lastIndexOf(".");
  if (dot > -1) return name.slice(dot + 1).toLowerCase();

  const type = String(file?.type || "").toLowerCase();
  if (type.includes("png")) return "png";
  if (type.includes("webp")) return "webp";
  if (type.includes("jpeg") || type.includes("jpg")) return "jpg";
  return "jpg";
}

async function uploadProfilePhotoIfNeeded({
  userId,
  bucket,
  file,
}) {
  if (!userId || !bucket || !file) return null;

  const ext = extFromFile(file);
  const path = `${userId}/profile_${Date.now()}.${ext}`;

  // Upload new
  const { error: upErr } = await supabase.storage
    .from(bucket)
    .upload(path, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: file.type || undefined,
    });

  if (upErr) throw upErr;

  // Public URL (bucket must be public)
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  const publicUrl = data?.publicUrl || "";

  const { data: signed, error: signedErr } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, 60 * 60 * 24);

  const signedUrl = !signedErr && signed?.signedUrl ? signed.signedUrl : "";
  return { path, publicUrl, signedUrl };
}

  // Save from EditIntro itself (REAL: preview → patient_profiles)
async function handleSave() {
  try {
    setSaveError("");
    setSaved(false);

    const {
      data: { user },
      error: authErr,
    } = await supabase.auth.getUser();

    if (authErr || !user?.id) {
      setSaveError(
        t("patient.editIntro.errors.signInToSave", "You need to be signed in to save changes."),
      );
      return;
    }

    // 3.5) Upload profile photo if user picked one (persist path + public url)
    const previousProfilePhotoPath = profile?.profile_photo_path || null;
    let nextProfilePhotoPath = pendingPhotoPath || previousProfilePhotoPath;
    if (avatarFile) {
      const uploaded = await uploadProfilePhotoIfNeeded({
        userId: user.id,
        bucket: PATIENT_AVATAR_BUCKET,
        file: avatarFile,
      });

      if (uploaded?.path) nextProfilePhotoPath = uploaded.path;

      try {
        setPreviewUrl(uploaded?.signedUrl || uploaded?.publicUrl || "");
      } catch {}

      // Persist avatar path immediately so it is never lost if another field fails later.
      if (nextProfilePhotoPath) {
        const { error: avatarPathErr } = await supabase
          .from("patient_profiles")
          .upsert(
            { user_id: user.id, profile_photo_path: nextProfilePhotoPath },
            { onConflict: "user_id" }
          );
        if (avatarPathErr) throw avatarPathErr;
      }
    }

    const preview = loadPatientIntroPreview() || {};

    const toText = (value) => {
      const text = textFromUnknown(value);
      return text || null;
    };

    // Build payload: preview first, then DB fallback (so we don't null-out existing)
    const payload = {
      user_id: user.id,

      // ✅ only ONE source of truth: store the PATH (private bucket)
      profile_photo_path: nextProfilePhotoPath || profile?.profile_photo_path || null,

      full_name: toText(
        preview.name ||
          preview.full_name ||
          [preview.firstName, preview.lastName].map(textFromUnknown).filter(Boolean).join(" ") ||
          profile?.full_name ||
          profile?.display_name ||
          [profile?.first_name, profile?.last_name].map(textFromUnknown).filter(Boolean).join(" ") ||
          ""
      ),
      date_of_birth: toText(
        preview.dobIso ||
          preview.date_of_birth ||
          tableDraft.date_of_birth ||
          profile?.date_of_birth ||
          ""
      ),

      phone: toText(
        preview.phone ||
        preview.phone_number ||
        profile?.phone ||
        profile?.phone_number ||
        authUser?.phone ||
        sessionUser?.phone ||
        ""
      ),
      email: toText(
        preview.email ||
        profile?.email ||
        authUser?.email ||
        sessionUser?.email ||
        ""
      ),

      // ⚠️ confirm your actual column name: your DB shows national_id_number
      national_id: toText(
        preview.nationalId ||
        preview.national_id ||
        preview.national_id_number ||
        preview.idPassport ||
        preview.idNumber ||
        preview.id_number ||
        profile?.national_id ||
        profile?.national_id_number ||
        ""
      ),
      national_id_number: toText(
        preview.nationalId ||
        preview.national_id ||
        preview.national_id_number ||
        preview.idPassport ||
        preview.idNumber ||
        preview.id_number ||
        profile?.national_id_number ||
        profile?.national_id ||
        ""
      ),

      street_address: toText(
        preview.addressLine ||
        preview.addressStreet ||
        profile?.street_address ||
        ""
      ),
      city: toText(
        preview.addressLine2 ||
        preview.city ||
        profile?.city ||
        ""
      ),
      postal_code: toText(
        preview.postalCode ||
        preview.addressPostalCode ||
        profile?.postal_code ||
        ""
      ),
      country: toText(
        preview.country ||
        preview.addressCountry ||
        profile?.country ||
        ""
      ),

      blood_type: toText(preview.bloodType || profile?.blood_type || ""),

      allergies: (() => {
        const a = listFromUnknown(preview.allergies);
        if (a.length) return a;
        return listFromUnknown(profile?.allergies);
      })(),

      medical_conditions: (() => {
        const c = normalizeConditionList(listFromUnknown(preview.conditions));
        if (c.length) return c.join(", "); // DB column is text
        return normalizeConditionList(listFromUnknown(profile?.medical_conditions)).join(", ");
      })(),

      organ_donor: (() => {
        const previewChoice = boolFromUnknown(preview.organDonor);
        if (previewChoice !== null) return previewChoice;
        const dbChoice = boolFromUnknown(profile?.organ_donor);
        return dbChoice !== null ? dbChoice : null;
      })(),

      languages: (() => {
        const l = listFromUnknown(preview.languages);
        return orderLanguages(l.length ? l : profile?.languages, preferredLanguage);
      })(),
    };

    // ✅ Upsert ensures row exists (onConflict user_id)
    const { error: upErr } = await supabase
      .from("patient_profiles")
      .upsert(payload, { onConflict: "user_id" });

    if (upErr) {
      setSaveError(
        upErr.message ||
          t("settings.edit.common.saveFailed", isFrench ? "Impossible d’enregistrer vos modifications." : "We couldn’t save your changes."),
      );
      return;
    }

    // ✅ refresh AuthContext profile so avatar + headers hydrate everywhere
    try {
      await refreshProfile?.();
    } catch {}

    // ✅ Reload profile immediately so UI stops “preview mirage”
    const { data: fresh, error: freshErr } = await supabase
      .from("patient_profiles")
      .select(
        [
          "profile_photo_path",
          "full_name",
          "date_of_birth",
          "phone",
          "phone_number",
          "phone_verified_at",
          "email",
          "email_verified_at",
          "national_id",
          "national_id_number",
          "verification_status",
          "street_address",
          "city",
          "postal_code",
          "country",
          "blood_type",
          "allergies",
          "medical_conditions",
          "organ_donor",
          "languages",
        ].join(",")
      )
      .eq("user_id", user.id)
      .maybeSingle();

    if (!freshErr) setProfile(fresh || null);

    if (
      previousProfilePhotoPath &&
      nextProfilePhotoPath &&
      previousProfilePhotoPath !== nextProfilePhotoPath
    ) {
      try {
        await supabase.storage.from(PATIENT_AVATAR_BUCKET).remove([previousProfilePhotoPath]);
      } catch {
        // non-fatal cleanup
      }
    }

    // keep preview state in sync
    setIntroPreview(loadPatientIntroPreview() || {});

    setSaved(true);

    // ✅ navigate, but don't keep timers that can fire after unmount
    nav("/patient/profile", { replace: true });
      } catch (e) {
    console.error("[Patient EditIntro] save error:", e);
    setSaveError(
      e?.message ||
        t("settings.edit.common.saveFailed", isFrench ? "Impossible d’enregistrer vos modifications." : "We couldn’t save your changes."),
    );
  }
}

  // ================================================================
// Render
// ================================================================
return (
  <div className="profile-page-shell w-full">
    {/* Full-width chrome: Top bar + cover */}
    <div className="afrimed-rail afrimed-rail--comfortable" data-testid="patient-edit-intro-app-bar">
      <TopBarCenter
        close
        onBack={() => nav(PATIENT_SETTINGS_PROFILE_ROUTE)}
        title={t("patient.editIntro.title", "Edit Intro")}
        right={
          <SaveChip
            label={t("settings.editIntro.saveProfile", "Save Profile")}
            disabled={uploading || loading || (avatarRemoved && !avatarFile)}
            onClick={handleSave}
          />
        }
      />

    </div>

    {/* Cover photo + overlaid avatar, following blueprint */}
    <div className="relative profile-cover-bleed -mt-4 md:-mt-5" data-testid="patient-edit-intro-cover">
      <CoverPhoto
        src={isDark ? COVER_IMAGE_DARK : COVER_IMAGE_LIGHT}
        heightClassName="h-[172px] sm:h-[172px] md:h-[280px] lg:h-[320px]"
        overlayClassName={
          isDark
            ? "bg-[linear-gradient(180deg,rgba(8,10,18,0.06)_0%,rgba(8,10,18,0.14)_38%,rgba(8,10,18,0.24)_68%,rgba(8,10,18,0.34)_100%)]"
            : ""
        }
      />

      <AvatarHeaderPositioned
        previewUrl={previewUrl}
        onClick={openPicker}
        editIconSrc={ICON_EDIT_SQUARE}
        mobileOffsetClassName="pt-[132px] sm:pt-[132px]"
        dataTestId="patient-edit-intro-avatar"
      />
    </div>

    {/* 950px content shell */}
    <div
      className="
        mt-12              /* 40px avatar overlap + prescribed 24px content gap */
        sm:mt-12
        md:mt-[116px]      /* tablet/desktop: push cards a bit lower */
        lg:mt-[110px]
        afrimed-rail afrimed-rail--comfortable pt-4 pb-[calc(128px+env(safe-area-inset-bottom))]
      "
    >
      {/* Sections stack */}
      <div className="space-y-10">
        
          {/* Personal Information ------------------------------------------------ */}
            <section>
              <h2 data-testid="patient-edit-intro-personal-information" className="text-title-medium text-on-surface-var mb-2">
                {t("patient.editIntro.sections.personalInfo", "Personal Information")}
              </h2>

            <Card className="overflow-hidden">
              <InfoRow
                label={t("doctor.editIntro.labels.name", "Name")}
                value={nameValue}
                isPlaceholder={nameIsPlaceholder}
                iconSrc={null}
                onClick={() => nav("/settings/patient/p/edit-name")}
              />

              <InsetDivider />

              <InfoRow
                label={t("doctor.editIntro.labels.dob", "Date of birth")}
                value={dobValue}
                isPlaceholder={dobIsPlaceholder}
                iconSrc={null}
                onClick={() => nav("/settings/patient/p/edit-dob")}
              />
            </Card>

            <p className="mt-2 text-body-small text-on-surface-var">
              {t(
                "patient.editIntro.verification.verified",
                "Verified by the official ID provided.",
              )}
            </p>
          </section>

          <FolaOnboardingCard
            role="patient"
            placement="patientEditIntro"
          />

          {/* Divider */}
          <RailDivider />

        {/* Contact data -------------------------------------------------------- */}
        <section>
          <h2 className="text-title-medium text-on-surface-var mb-2">
            {t("patient.editIntro.sections.contactData", "Contact data")}
          </h2>

          <Card className="overflow-hidden">
            <InfoRow
              iconSrc={ICON_PHONE}
              label={t("patient.profile.labels.phone", "Phone")}
              value={phoneValue}
              isPlaceholder={phoneIsPlaceholder}
              extraRight={
                showPhoneVerified ? (
                  <VerifiedChip alt={t("patient.profile.verified", "Verified")} />
                ) : showPhonePending ? (
                  <PendingChip alt={t("patient.profile.pending", "Pending")} />
                ) : null
              }
              onClick={() => nav("/settings/patient/p/edit-phone")}
            />

            <InsetDivider />

            <InfoRow
              iconSrc={ICON_EMAIL}
              label={t("patient.profile.labels.email", "Email")}
              value={emailValue}
              isPlaceholder={emailIsPlaceholder}
              extraRight={
                showEmailVerified ? (
                  <VerifiedChip alt={t("patient.profile.verified", "Verified")} />
                ) : null
              }
              onClick={() => nav("/settings/patient/p/edit-email")}
            />

            <InsetDivider />

            <InfoRow
              iconSrc={ICON_NATIONAL_ID}
              label={t("patient.profile.labels.nationalId", "National ID / Passport")}
              value={idValue}
              isPlaceholder={idIsPlaceholder}
              extraRight={
                idVerified
                  ? <VerifiedChip alt={t("patient.profile.verified", "Verified")} />
                  : idPending && !idIsPlaceholder
                  ? <PendingChip alt={t("patient.profile.pending", "Pending")} />
                  : null
              }
              onClick={() => nav("/settings/patient/p/edit-id-passport")}
            />
          </Card>
        </section>

          {/* Divider */}
          <RailDivider />
        
      {/* Health insurance --------------------------------------------------- */}
      <section>
        <h2 className="text-title-medium text-on-surface-var mb-2">
          {t("patient.profile.sections.insurance", "Health insurance")}
        </h2>

        <Card className="mt-2 overflow-hidden">
          <InfoRow
            iconSrc={ICON_HEALTH_INSURANCE}
            label={t("patient.profile.sections.insurance", "Health insurance")}
            value={
              insuranceSummary
                ? insuranceSummary
                : insuranceLoaded
                ? t("common.comingSoon", "Coming soon")
                : t("common.loading", "Loading...")
            }
            isPlaceholder={!insuranceSummary}
            onClick={() =>
              nav("/settings/insurance", { state: { returnTo: location.pathname } })
            }
          />

          {/* Divider (same as the rest of EditIntro) */}
          <InsetDivider />

        {/* Dependants (mini list, 0–3 rows) — IMPORTANT: NOT a nested card */}
          {!dependantsLoaded && (
            <InfoRow
              iconSrc={ICON_DEPENDANT_CHILD}
              label={t("patient.profile.sections.dependants", "Dependants")}
              value={t("common.loading", "Loading...")}
              isPlaceholder
              onClick={() =>
                nav("/settings/dependants", { state: { returnTo: location.pathname } })
              }
            />
          )}

        {dependantsLoaded && safeDependantsRows.length > 0 && (
          <div>
            {safeDependantsRows.slice(0, 3).map((d, idx) => {
              const isLast = idx === Math.min(2, safeDependantsRows.length - 1);

                const first = String(d?.first_name || "").trim();
                const last = String(d?.last_name || "").trim();
                const fullName =
                  `${first} ${last}`.trim() ||
                  t("patient.profile.dependantFallback", "Dependant");

                // We ONLY show Pending or Verified (no “Unverified” wording)
                const status = String(d?.identity_verification_status || d?.verification_status || "").toLowerCase();
                const rightBadge =
                  status === "verified" ? (
                    <VerifiedChip alt={t("patient.profile.verified", "Verified")} />
                  ) : (
                    <PendingChip alt={t("patient.profile.pending", "Pending")} />
                  );

                // Relationship line (optional). If you don’t want it, set to "".
                const relationship = String(d?.relationship || "").trim();

                return (
                  <div key={d?.id || `${idx}`}>
                    <InfoRow
                      iconSrc={resolveDependantAvatarSrc(d)}
                      iconAvatar
                      label={
                        relationship ||
                        t("patient.profile.dependantFallback", "Dependant")
                      }
                      value={fullName}
                      extraRight={rightBadge}
                      onClick={() =>
                        nav("/settings/dependants", {
                          state: { returnTo: location.pathname, openId: d?.id },
                        })
                      }
                    />

                    {!isLast && <InsetDivider />}
                  </div>
                );
              })}

              {safeDependantsRows.length > 3 && (
                <>
                  <InsetDivider />
                  <button
                    type="button"
                    className="w-full px-4 py-3 text-left text-primary text-body-medium"
                    onClick={() =>
                      nav("/settings/dependants", {
                        state: { returnTo: location.pathname },
                      })
                    }
                  >
                    {t("common.showAll", "Show all")} ({safeDependantsRows.length})
                  </button>
                </>
              )}
            </div>
          )}
        </Card>

        <p className="mt-2 text-body-small text-on-surface-var">
          {t(
            "patient.profile.insuranceSupport",
            "Add your provider and membership details to speed up claims and billing.",
          )}
        </p>
      </section>

        {/* Divider */}
        <RailDivider />

      {/* Residence address --------------------------------------------------- */}
          <section>
            <h2 className="text-title-medium text-on-surface-var mb-2">
              {t("patient.editIntro.sections.residenceAddress", isFrench ? "Adresse de résidence" : "Residence address")}
            </h2>

            <Card className="overflow-hidden">
              <InfoRow
                iconSrc={ICON_ADDRESS}
                label={t("patient.profile.labels.street", isFrench ? "Rue" : "Street")}
                value={streetValue}
                isPlaceholder={streetIsPlaceholder}
                onClick={() => nav("/settings/patient/p/edit-address")}
              />

              <InsetDivider />

              <InfoRow
                iconSrc={ICON_POSTAL}
                label={t("patient.profile.labels.postalCode", isFrench ? "Code postal" : "Postal code")}
                value={postalValue}
                isPlaceholder={postalIsPlaceholder}
                onClick={() => nav("/settings/patient/p/edit-address")}
              />

              <InsetDivider />

              <InfoRow
                iconSrc={ICON_DISTANCE}
                label={t("patient.profile.labels.country", isFrench ? "Pays" : "Country")}
                value={countryValue}
                isPlaceholder={countryIsPlaceholder}
                onClick={() => nav("/settings/patient/p/edit-address")}
              />
            </Card>
          </section>

          {/* Divider */}
          <RailDivider />

        {/* Languages ---------------------------------------------------------------- */}
        <section>
          <h2 className="text-title-medium text-on-surface-var mb-2">
            {t("patient.profile.sections.languages", isFrench ? "Langues" : "Languages")}
          </h2>

          {hasLanguages ? (
            <Card className="overflow-hidden">
              {safeLanguagesUi.map((l, idx) => (
                <div key={l.id}>
	                  <div className="w-full flex items-center px-4 py-3">
	                    <div className="flex-1 min-w-0">
	                      {l.preferred ? (
	                        <div className="text-label-medium text-on-surface-var">
	                          {t("settings.subflows.languages.preferredLabel", isFrench ? "Langue préférée / principale" : "Preferred / primary language")}
	                        </div>
	                      ) : null}
	                      <div className="text-body-large text-on-surface truncate">
	                        {l.label}
	                      </div>
                    </div>

                    <button
                      type="button"
                      aria-label={`${t("common.edit", "Edit")} ${l.label}`}
                      className="ml-3 inline-flex h-6 w-6 items-center justify-center text-on-surface-var"
                      onClick={() => openLanguageMenu(l.label)}
                    >
                      <img
                        src={ICON_MORE_VERT}
                        alt=""
                        className="h-5 w-5"
                      />
                    </button>
                  </div>

                  {idx < languagesUi.length - 1 && (
                    <InsetDivider />
                  )}
                </div>
              ))}
            </Card>
          ) : (
            <Card className="p-4 text-body-large text-on-surface-var">
              {t(
                "patient.editIntro.languages.empty",
                isFrench
                  ? "Ajoutez les langues que vous parlez afin que les cliniciens puissent communiquer avec vous dans la langue appropriée."
                  : "Add the languages you speak so clinicians can choose what suits you best.",
              )}
            </Card>
          )}

          {/* Add language – tonal full-width AfriMed button */}
          <button
            type="button"
            className="mt-6 w-full rounded-xl bg-[rgba(77,163,255,0.12)] px-4 py-3 inline-flex items-center justify-center gap-2"
            onClick={() => nav("/settings/patient/p/languages")}
          >
            <span className="inline-flex h-4 w-4 items-center justify-center text-primary">
              <span className="text-body-large">＋</span>
            </span>
            <span className="text-primary text-label-large">
              {t("patient.editIntro.actions.addLanguage", isFrench ? "Ajouter une langue" : "Add language")}
            </span>
          </button>
        </section>

          {/* Divider */}
          <RailDivider />

          {/* Medical information ------------------------------------------------- */}
          <section>
            <h2 className="text-title-medium text-on-surface-var mb-2">
              {t("patient.editIntro.sections.medicalInfo", isFrench ? "Informations médicales" : "Medical information")}
            </h2>

            <Card className="overflow-hidden">
              <InfoRow
                iconSrc={ICON_BLOODTYPE}
                label={t("patient.profile.labels.bloodType", isFrench ? "Groupe sanguin" : "Blood type")}
                value={bloodValue}
                isPlaceholder={bloodIsPlaceholder}
                onClick={() => nav("/settings/patient/p/blood-type")}
              />

              <InsetDivider />

              <InfoRow
                iconSrc={ICON_ALLERGY}
                label={t("patient.profile.labels.allergies", "Allergies")}
                value={allergiesValue}
                isPlaceholder={allergiesIsPlaceholder}
                onClick={() => nav("/settings/patient/p/allergies")}
              />

              <InsetDivider />

              <InfoRow
                iconSrc={ICON_CONDITIONS}
                label={t("patient.profile.labels.chronicConditions", isFrench ? "Antécédents médicaux" : "Medical conditions")}
                value={conditionsValue}
                isPlaceholder={conditionsIsPlaceholder}
                onClick={() => nav("/settings/patient/p/conditions")}
              />

              <InsetDivider />

              <InfoRow
                iconSrc={ICON_PULMONOLOGY}
                label={t("patient.profile.labels.organDonor", isFrench ? "Don d’organes" : "Organ donor")}
                value={donorValue}
                isPlaceholder={donorIsPlaceholder}
                onClick={() => nav("/settings/patient/p/organ-donor")}
              />

              <InsetDivider />

              <InfoRow
                iconSrc={ICON_HEIGHT_WEIGHT}
                label={t("patient.profile.labels.heightWeight", isFrench ? "Taille et poids" : "Height & Weight")}
                value={heightWeightValue}
                isPlaceholder={heightWeightIsPlaceholder}
                onClick={() =>
                  nav("/settings/patient/p/height-weight", {
                    state: { returnTo: "/settings/patient/p/edit-intro" },
                  })
                }
              />
            </Card>
          </section>
        </div>
      </div>

    {/* Languages kebab menu – Edit / Delete ----------------------------------- */}
      {languageMenuOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(15,23,42,0.35)] backdrop-blur-sm"
          onClick={closeLanguageMenu}
        >
          <div
            className="w-[90vw] max-w-[260px] rounded-2xl bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0_18px_45px_rgba(15,23,42,0.18)] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 pt-3 pb-2">
              <div className="text-body-large text-on-surface">
                {selectedLanguage}
              </div>
            </div>

            {/* Edit */}
            <button
              type="button"
              className="w-full flex justify-start px-4 pt-1 pb-3 focus:outline-none"
              onClick={() => {
                closeLanguageMenu();
                nav("/settings/patient/p/languages");
              }}
            >
              <span className="inline-flex items-center gap-2 rounded-xl px-3 py-2 hover:bg-[rgba(29,79,215,0.12)] active:bg-[rgba(29,79,215,0.16)]">
                <img src={ICON_EDIT_SQUARE} alt="" className="h-6 w-6" />
                <span className="text-label-large text-primary">
                  {t("common.edit", "Edit")}
                </span>
              </span>
            </button>

            {/* Delete */}
            <button
              type="button"
              className="w-full flex justify-start px-4 pt-1 pb-3 focus:outline-none"
              onClick={() => {
                const current = loadPatientIntroPreview() || {};
                const next = {
                  ...current,
                  languages: (current.languages || []).filter((x) => {
                    const label =
                      typeof x === "string"
                        ? x
                        : x.label || x.language || x.name || "";
                    return label !== selectedLanguage;
                  }),
                };
                savePatientIntroPreview(next);
                setIntroPreview(next);
                setLanguageMenuOpen(false);
                setSaved(true);
              }}
            >
              <span className="inline-flex items-center gap-2 rounded-xl px-3 py-2 hover:bg-[rgba(249,115,22,0.10)] active:bg-[rgba(249,115,22,0.16)]">
                <img src={ICON_DELETE} alt="" className="h-6 w-6" />
                <span className="text-label-large text-on-surface">
                  {t("common.delete", "Delete")}
                </span>
              </span>
            </button>
          </div>
        </div>
      )}

    {/* Bottom sheet: photo picker */}
      <Sheet open={pickerOpen} onClose={closePicker}>
        <div className="mx-auto w-full max-w-[412px] relative z-[60] pointer-events-auto">
          {/* Title (left aligned, collision-safe) */}
          <div className="pt-6 pb-6">
            <p className="text-title-large text-on-surface mx-4">
              {t("patient.editIntro.photoSheet.title", "Edit your profile photo")}
            </p>
          </div>

          <div className="rounded-[16px] overflow-hidden bg-[rgb(var(--clr-surface-cont-highest))]">
            {/* Upload image */}
            <button
              type="button"
              onClick={handleUploadClick}
              disabled={uploading || loading}
              className="w-full flex items-center gap-3 px-2 py-3 disabled:opacity-60"
            >
              <img src={ICON_UPLOAD_IMAGE} alt="" className="h-5 w-5 ml-2" />
              <span className="flex-1 text-left text-body-large text-on-surface">
                {t("patient.editIntro.photoSheet.uploadImage", "Upload image")}
              </span>
              <span className="mr-2 text-body-medium text-on-surface-var">›</span>
            </button>

            {/* divider aligned with content */}
            <div className="h-px bg-[rgba(24,50,119,0.10)] ml-4 mr-3" />

            {/* Take Photo */}
            <button
              type="button"
              onClick={handleTakePhotoClick}
              disabled={uploading || loading}
              className="w-full flex items-center gap-3 px-2 py-3 disabled:opacity-60"
            >
              <img src={ICON_TAKE_PHOTO} alt="" className="h-5 w-5 ml-2" />
              <span className="flex-1 text-left text-body-large text-on-surface">
                {t("patient.editIntro.photoSheet.takePhoto", "Take photo")}
              </span>
              <span className="mr-2 text-body-medium text-on-surface-var">›</span>
            </button>

            {hasCustomAvatar ? <>
              <div className="h-px bg-[rgba(24,50,119,0.10)] ml-4 mr-3" />
              <button
                type="button"
                onClick={() => setRemoveAvatarConfirmOpen(true)}
                disabled={uploading || loading}
                className="w-full flex items-center gap-3 px-2 py-3 disabled:opacity-60"
              >
                <img src={ICON_DELETE_CRITICAL} alt="" className="h-5 w-5 ml-2" />
                <span className="flex-1 text-left text-body-large text-[rgb(var(--schemes-critical))]">
                  {t("common.removeImage", "Remove image")}
                </span>
              </button>
            </> : null}

            {/* bottom hairline */}
            <div className="h-px bg-[rgba(24,50,119,0.06)]" />
          </div>
        </div>
      </Sheet>

      <BasicDialogCritical
        open={removeAvatarConfirmOpen}
        title={t("patient.editIntro.removePhoto.title", "Remove profile picture?")}
        body={t("patient.editIntro.removePhoto.body", "Your current profile picture will be removed.")}
        cancelText={t("common.cancel", "Cancel")}
        okText={t("common.removeImage", "Remove image")}
        onCancel={() => setRemoveAvatarConfirmOpen(false)}
        onOk={removeProfileAvatar}
        disableBackdropClose={uploading}
      />

    {/* Hidden file inputs backing the bottom sheet actions */}
    <input
      ref={fileInputRef}
      type="file"
      accept="image/*"
      style={{
        position: "absolute",
        width: 1,
        height: 1,
        padding: 0,
        margin: -1,
        overflow: "hidden",
        clip: "rect(0,0,0,0)",
        whiteSpace: "nowrap",
        border: 0,
      }}
      onChange={onAvatarFilePicked}
    />

    <input
      ref={cameraInputRef}
      type="file"
      accept="image/*"
      capture="environment"
      style={{
        position: "absolute",
        width: 1,
        height: 1,
        padding: 0,
        margin: -1,
        overflow: "hidden",
        clip: "rect(0,0,0,0)",
        whiteSpace: "nowrap",
        border: 0,
      }}
      onChange={onAvatarFilePicked}
    />

    {showAvatarCrop && avatarSrc && (
      <CoverCropModal
        src={avatarSrc}
        aspect={1 / 1}
        onCancel={() => {
          setShowAvatarCrop(false);
          setAvatarSrc(null);
        }}
        onConfirm={async (blob) => {
          setShowAvatarCrop(false);
          setAvatarSrc(null);
          setSaveError("");

          try {
            setUploading(true);
            const contentType = blob?.type || "image/jpeg";
            const ext = contentType.includes("png") ? "png" : "jpg";
            const croppedFile = new File([blob], `profile_crop_${Date.now()}.${ext}`, {
              type: contentType,
            });

            setAvatarFile(croppedFile);
            setPendingPhotoPath("");

            const toDataUrl = (file) =>
              new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result || ""));
                reader.onerror = reject;
                reader.readAsDataURL(file);
              });

            const dataUrl = await toDataUrl(croppedFile);
            if (dataUrl) setPreviewUrl(dataUrl);

            // (Optional) localhost DEV override only
            try {
              const host = window.location.hostname;
              const isLocal =
                host === "localhost" ||
                host === "127.0.0.1" ||
                host === "0.0.0.0" ||
                host.endsWith(".local");

              if (import.meta.env.DEV && isLocal) {
                if (dataUrl) {
                  localStorage.setItem(DEV_PHOTO_KEY, dataUrl);
                }
              }
            } catch {}
          } catch (e) {
            console.error(e);
            setSaveError(
              t(
                "patient.editIntro.errors.savePhoto",
                isFrench ? "Impossible d’enregistrer votre photo de profil. Réessayez plus tard." : "We couldn’t save your profile picture. Try again later.",
              ),
            );
          } finally {
            setUploading(false);
          }
        }}
      />
    )}

    {/* Snackbars */}
      <Snackbar
        open={saved}
        onClose={() => setSaved(false)}
        duration={2200}
      >
        {t("settings.editIntro.profileSaved", "Profile saved")}
      </Snackbar>

      {saveError && (
        <Snackbar
          open={Boolean(saveError)}
          onClose={() => setSaveError("")}
          duration={2800}
          variant="error"
        >
          {saveError}
        </Snackbar>
      )}
    </div>
  );
}
